import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileDropzone } from '@/components/shared/file-dropzone';
import { BUILT_IN_FUND_TYPES } from '@/components/shared/fund-type-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Listbox } from '@/components/ui/listbox';
import { Stepper } from '@/components/ui/stepper';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatNumber } from '@/utils/format';

import {
  buildFundImportMapping,
  FUND_IMPORT_FIELD_LABEL_KEYS,
  FUND_IMPORT_FIELDS,
  MAX_FUND_IMPORT_ROWS,
  parseCsv,
  prepareFundImportRows,
  REQUIRED_FUND_IMPORT_FIELDS,
  SAMPLE_FUND_CSV,
  type FundImportField,
  type FundImportRowIssue,
  type FundImportRowPayload,
  type PreparedFundImportRow,
} from './fund-import-csv';

type Step = 0 | 1 | 2 | 3;

interface ImportResultRow {
  index: number;
  ok: boolean;
  fundId?: string;
  error?: string;
  params?: Record<string, unknown>;
}

interface FundImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCurrency: string;
}

const displayLabel = (row: PreparedFundImportRow, mapping: Record<string, FundImportField | ''>) => {
  if (row.payload) return row.payload.name;
  const nameHeader = Object.entries(mapping).find(([, field]) => field === 'name')?.[0];
  const codeHeader = Object.entries(mapping).find(([, field]) => field === 'code')?.[0];
  const name = nameHeader ? (row.source[nameHeader] ?? '') : '';
  const code = codeHeader ? (row.source[codeHeader] ?? '') : '';
  const label = name || code;
  return label || '—';
};

export const FundImportDialog = ({ open, onOpenChange, defaultCurrency }: FundImportDialogProps) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, FundImportField | ''>>({});
  const [results, setResults] = useState<ImportResultRow[] | null>(null);

  const customTypes = useQuery({
    queryKey: ['giving', 'fund-custom-types'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string; code: string }> }>(
        '/giving/fund-custom-types?pageSize=100',
      ),
    enabled: open,
  });

  const existingFunds = useQuery({
    queryKey: ['giving', 'funds', 'import-codes'],
    queryFn: async () => {
      const codes = new Set<string>();
      let page = 1;
      let totalPages = 1;
      do {
        const response = await api.get<{
          data: Array<{ code: string }>;
          page: { totalPages: number };
        }>('/giving/funds', { query: { page, pageSize: 100, sort: 'code' } });
        for (const row of response.data) {
          codes.add(row.code.trim().toUpperCase());
        }
        totalPages = response.page.totalPages;
        page += 1;
      } while (page <= totalPages);
      return codes;
    },
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const reset = () => {
    setStep(0);
    setFileName(null);
    setFileError(null);
    setHeaders([]);
    setRawRows([]);
    setMapping({});
    setResults(null);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const fundTypeLabels = useMemo(
    () =>
      Object.fromEntries(
        BUILT_IN_FUND_TYPES.map((type) => [type, t(`fundTypes.${type}`)]),
      ) as Record<string, string>,
    [t],
  );

  const prepared = useMemo(
    () =>
      prepareFundImportRows({
        rows: rawRows,
        mapping,
        customTypes: customTypes.data?.data ?? [],
        fundTypeLabels,
        existingCodes: existingFunds.data ?? new Set<string>(),
        defaultCurrency,
      }),
    [rawRows, mapping, customTypes.data?.data, fundTypeLabels, existingFunds.data, defaultCurrency],
  );

  const readyRows = prepared.filter(
    (row): row is PreparedFundImportRow & { payload: FundImportRowPayload } =>
      Boolean(row.payload),
  );
  const problemRows = prepared.filter((row) => row.issues.length > 0);

  const mappedRequired = REQUIRED_FUND_IMPORT_FIELDS.every((field) =>
    Object.values(mapping).includes(field),
  );

  const fieldOptions = [
    { value: '', label: t('funds.import.fields.skip') },
    ...FUND_IMPORT_FIELDS.map((field) => ({
      value: field,
      label: t(FUND_IMPORT_FIELD_LABEL_KEYS[field]),
    })),
  ];

  const issueLabel = (issue: FundImportRowIssue) => t(`funds.import.issues.${issue}`);

  const importMutation = useMutation({
    mutationFn: (rows: FundImportRowPayload[]) =>
      api.post<{ results: ImportResultRow[] }>('/giving/funds/import', { rows }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds', 'active'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds', 'insights'] });
      void queryClient.invalidateQueries({ queryKey: ['programs', 'registration-funds'] });
      const succeeded = data.results.filter((row) => row.ok).length;
      if (succeeded > 0 && data.results.every((row) => row.ok)) {
        toast.success(t('funds.import.toast.done', { count: succeeded }));
        onOpenChange(false);
        return;
      }
      toast.success(t('funds.import.toast.done', { count: succeeded }));
      setResults(data.results);
      setStep(3);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const onFiles = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setFileError(null);

    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      setFileError(t('funds.import.upload.invalidType'));
      return;
    }

    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.headers.length === 0 || parsed.rows.length === 0) {
        setFileError(t('funds.import.upload.empty'));
        return;
      }
      if (parsed.rows.length > MAX_FUND_IMPORT_ROWS) {
        setFileError(
          t('funds.import.upload.tooMany', { max: formatNumber(MAX_FUND_IMPORT_ROWS) }),
        );
        return;
      }
      setFileName(file.name);
      setHeaders(parsed.headers);
      setRawRows(parsed.rows);
      setMapping(buildFundImportMapping(parsed.headers));
      setStep(1);
    } catch {
      setFileError(t('funds.import.upload.readFailed'));
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_FUND_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'funds-import-sample.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const steps = [
    { id: 'upload', label: t('funds.import.steps.upload') },
    { id: 'map', label: t('funds.import.steps.map') },
    { id: 'preview', label: t('funds.import.steps.preview') },
    { id: 'results', label: t('funds.import.steps.results') },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>{t('funds.import.title')}</DialogTitle>
          <DialogDescription>{t('funds.import.description')}</DialogDescription>
        </DialogHeader>

        <Stepper
          label={t('funds.import.stepperLabel')}
          current={step}
          steps={steps}
          className="shrink-0"
        />

        <DialogBody>
          {step === 0 ? (
            <div className="space-y-4">
              <p className="text-body-sm text-fg-secondary">{t('funds.import.upload.notContributions')}</p>
              <FileDropzone
                accept=".csv,text/csv"
                acceptLabel={t('funds.import.upload.accept')}
                maxBytes={2 * 1024 * 1024}
                maxSizeLabel={t('funds.import.upload.maxSize')}
                error={fileError ?? undefined}
                labels={{
                  prompt: t('funds.import.upload.prompt'),
                  remove: tCommon('action.remove'),
                }}
                files={fileName ? [{ name: fileName }] : undefined}
                onRemove={() => {
                  setFileName(null);
                  setHeaders([]);
                  setRawRows([]);
                  setMapping({});
                  setFileError(null);
                }}
                onFiles={(files) => {
                  void onFiles(files);
                }}
              />
              <div className="flex justify-end">
                <Button type="button" variant="ghost" size="sm" onClick={downloadSample}>
                  {t('funds.import.upload.downloadSample')}
                </Button>
              </div>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="space-y-4">
              <p className="text-body-sm text-fg-secondary">{t('funds.import.map.help')}</p>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[28rem] text-left text-body-sm">
                  <thead className="bg-surface-sunken text-fg-secondary">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('funds.import.map.csvColumn')}</th>
                      <th className="px-3 py-2 font-medium">{t('funds.import.map.trinityField')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {headers.map((header) => (
                      <tr key={header} className="border-t border-border">
                        <td className="px-3 py-2 align-middle font-medium text-fg">
                          {header || '—'}
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <Listbox
                            id={`fund-map-${header}`}
                            value={mapping[header] ?? ''}
                            onChange={(value) => {
                              const nextField = (value ?? '') as FundImportField | '';
                              setMapping((prev) => {
                                const next = { ...prev, [header]: nextField };
                                if (nextField) {
                                  for (const key of Object.keys(next)) {
                                    if (key !== header && next[key] === nextField) next[key] = '';
                                  }
                                }
                                return next;
                              });
                            }}
                            options={fieldOptions}
                            searchable
                            searchPlaceholder={t('funds.import.map.searchFields')}
                            emptyMessage={t('funds.import.map.noFieldMatch')}
                            popoverPortal="dialog"
                            clearable={false}
                            aria-label={t('funds.import.map.trinityFieldForColumn', {
                              column: header,
                            })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!mappedRequired ? (
                <p role="alert" className="text-caption text-danger-fg">
                  {t('funds.import.map.requiredMissing')}
                </p>
              ) : null}
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-4">
              <p className="text-body-sm text-fg-secondary">
                {t('funds.import.preview.summary', {
                  ready: readyRows.length,
                  problems: problemRows.length,
                })}
              </p>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[36rem] text-left text-body-sm">
                  <thead className="bg-surface-sunken text-fg-secondary">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('funds.columns.name')}</th>
                      <th className="px-3 py-2 font-medium">{t('funds.columns.code')}</th>
                      <th className="px-3 py-2 font-medium">{t('funds.columns.type')}</th>
                      <th className="px-3 py-2 font-medium">{t('funds.import.preview.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prepared.slice(0, 50).map((row) => (
                      <tr key={row.index} className="border-t border-border">
                        <td className="px-3 py-2 font-medium text-fg">
                          {displayLabel(row, mapping)}
                        </td>
                        <td className="px-3 py-2 text-fg-secondary">
                          {row.payload?.code ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-fg-secondary">
                          {(() => {
                            if (!row.payload?.fundType) return '—';
                            if (row.payload.fundType !== 'custom') {
                              return t(`fundTypes.${row.payload.fundType}`);
                            }
                            return (
                              row.payload.customTypeName ??
                              customTypes.data?.data.find(
                                (type) => type.id === row.payload?.customTypeId,
                              )?.name ??
                              t('fundTypes.custom')
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2">
                          {row.issues.length === 0 ? (
                            <Badge variant="success">{t('funds.import.preview.ready')}</Badge>
                          ) : (
                            <span className="text-caption text-danger-fg">
                              {row.issues.map(issueLabel).join(' · ')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {prepared.length > 50 ? (
                <p className="text-caption text-fg-tertiary">
                  {t('funds.import.preview.truncated', {
                    shown: formatNumber(50),
                    total: formatNumber(prepared.length),
                  })}
                </p>
              ) : null}
            </div>
          ) : null}

          {step === 3 && results ? (
            <div className="space-y-4">
              <p className="text-body-sm text-fg-secondary">
                {t('funds.import.results.summary', {
                  succeeded: results.filter((row) => row.ok).length,
                  failed: results.filter((row) => !row.ok).length,
                })}
              </p>
              {results.some((row) => !row.ok) ? (
                <ul className="space-y-2">
                  {results
                    .filter((row) => !row.ok)
                    .slice(0, 40)
                    .map((row) => {
                      const preparedRow = readyRows[row.index];
                      const label = preparedRow
                        ? preparedRow.payload.name
                        : t('funds.import.results.rowLabel', {
                            index: formatNumber(row.index + 1),
                          });
                      return (
                        <li
                          key={row.index}
                          className="rounded-md border border-border px-3 py-2 text-body-sm"
                        >
                          <span className="font-medium text-fg">{label}</span>
                          <span className="mt-0.5 block text-caption text-danger-fg">
                            {row.error
                              ? tErrors(row.error, row.params)
                              : tErrors('FUND_IMPORT_ROW_FAILED')}
                          </span>
                        </li>
                      );
                    })}
                </ul>
              ) : (
                <p className="text-body-sm text-fg">{t('funds.import.results.allGood')}</p>
              )}
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter>
          {step === 0 ? (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {tCommon('action.cancel')}
            </Button>
          ) : null}

          {step === 1 ? (
            <>
              <Button type="button" variant="outline" onClick={() => setStep(0)}>
                {tCommon('action.back')}
              </Button>
              <Button type="button" disabled={!mappedRequired} onClick={() => setStep(2)}>
                {tCommon('action.next')}
              </Button>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                disabled={importMutation.isPending}
              >
                {tCommon('action.back')}
              </Button>
              <Button
                type="button"
                loading={importMutation.isPending}
                disabled={readyRows.length === 0}
                onClick={() => importMutation.mutate(readyRows.map((row) => row.payload))}
              >
                {t('funds.import.preview.confirm', { count: readyRows.length })}
              </Button>
            </>
          ) : null}

          {step === 3 ? (
            <Button type="button" onClick={() => onOpenChange(false)}>
              {tCommon('action.close')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
